import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  CircularProgress,
  Tooltip,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import FlagIcon from "@mui/icons-material/Flag";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getAlarmFiles, getAlarmPage, getAlarmTypeList } from "../utility";
import EventDetailModal from "../Event/EventDetailModal";

const PAGE_SIZE = 8;

const ALARM_TYPE_MAP = {
  forwardCollisionWarning: "Forward Collision",
  laneChangeWarning: "Lane Departure",
  driverFatigue: "Driver Fatigue",
  overspeed: "Overspeed",
  geofence: "Geofence",
  routeDeviation: "Route Deviation",
  tampering: "Tampering",
};
const toDisplayType = (raw) => ALARM_TYPE_MAP[raw] ?? raw ?? "—";
const OPERATE_MAP = { 1: "Completed", 2: "Pending", 3: "Processing" };
const toStatus = (op) => OPERATE_MAP[op] ?? "Completed";
const CAM_LABELS = ["CH1", "CH2", "CH3", "CH4"];

function buildCamSlots(fileList = []) {
  const slots = [null, null, null, null];
  for (const f of fileList) {
    const m = f.fileUrl?.match(/_0(\d)_0[02]_/);
    if (m) {
      const idx = parseInt(m[1], 10);
      if (idx >= 0 && idx <= 3) slots[idx] = f;
    }
  }
  return slots;
}

const defaultRange = () => {
  const end = new Date();
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);
  const toLocal = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };
  return { startDate: toLocal(start), endDate: toLocal(end) };
};

// ─── Mini cam thumbnail ─────────────────────────────────────────────────────
const CamThumb = ({ file, index }) => {
  const [imgErr, setImgErr] = useState(false);

  if (!file) {
    return (
      <Box
        sx={{
          flex: 1,
          aspectRatio: "4/3",
          background: "#eef0f6",
          borderRadius: "5px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 13, color: "#b6bfd4" }} />
      </Box>
    );
  }

  if (file.fileType === "02") {
    return (
      <Box
        sx={{
          flex: 1,
          aspectRatio: "4/3",
          background: "#0e1b3a",
          borderRadius: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PlayCircleIcon sx={{ fontSize: 18, color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        aspectRatio: "4/3",
        borderRadius: "5px",
        overflow: "hidden",
        background: "#e3e6ee",
        position: "relative",
      }}
    >
      {imgErr ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <BrokenImageIcon sx={{ fontSize: 13, color: "#9aa3ba" }} />
        </Box>
      ) : (
        <Box
          component="img"
          src={file.fileUrl}
          alt={CAM_LABELS[index]}
          onError={() => setImgErr(true)}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: 2,
          left: 3,
          fontSize: "7px",
          fontWeight: 700,
          letterSpacing: "0.02em",
          background: "rgba(8,13,30,0.55)",
          color: "#fff",
          px: "4px",
          py: "1px",
          borderRadius: "3px",
        }}
      >
        {CAM_LABELS[index]}
      </Box>
    </Box>
  );
};

const StatusDot = ({ value }) => {
  const map = {
    Completed: "#16a34a",
    Pending: "#d97706",
    Processing: "#2563eb",
  };
  const c = map[value] ?? "#6b7280";
  return (
    <Stack direction="row" alignItems="center" spacing={0.6}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
      <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: c }}>
        {value}
      </Typography>
    </Stack>
  );
};

// ─── Compact event card ─────────────────────────────────────────────────────
const MiniEventCard = ({ record, fileMap, onClick }) => {
  const camSlots = buildCamSlots(fileMap[record.alarmId] ?? []);
  const status = toStatus(record.operate);
  const invalid = record.invalidStatus === 1;

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        border: "1px solid #e7eaf2",
        borderRadius: "10px",
        p: "10px",
        cursor: "pointer",
        background: "#fff",
        transition: "border-color .15s, box-shadow .15s, transform .1s",
        "&:hover": {
          borderColor: "#082D6B55",
          boxShadow: "0 4px 14px rgba(8,45,107,0.10)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: "6px" }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 700,
              color: "#082D6B",
              lineHeight: 1.3,
            }}
            noWrap
          >
            {toDisplayType(record.alarmType)}
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "#8a93a8", mt: "1px" }}>
            {record.alarmTime}
          </Typography>
        </Box>
        {invalid && (
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: "3px",
              px: "6px",
              py: "1px",
              borderRadius: "10px",
              background: "#fce8e6",
              color: "#b91c1c",
              fontSize: "9.5px",
              fontWeight: 700,
            }}
          >
            <FlagIcon sx={{ fontSize: 9 }} />
            Invalid
          </Box>
        )}
      </Stack>

      <Box sx={{ display: "flex", gap: "4px", mb: "7px" }}>
        {camSlots.map((file, i) => (
          <CamThumb key={i} file={file} index={i} />
        ))}
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pt: "6px", borderTop: "1px solid #f0f2f7" }}
      >
        <Typography sx={{ fontSize: "9.5px", color: "#a3aabd" }}>
          {record.fileNum ?? 0}/{record.expFileNum ?? 4} files
        </Typography>
        <StatusDot value={status} />
      </Stack>
    </Paper>
  );
};

const EventsSlidePanel = ({ deviceId, deviceName, onClose }) => {
  const [{ startDate, endDate }, setRange] = useState(defaultRange);
  const [alarmTypes, setAlarmTypes] = useState([]);
  const [alertType, setAlertType] = useState("");
  const [status, setStatus] = useState("");

  const [records, setRecords] = useState([]);
  const [fileMap, setFileMap] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalRecord, setModalRecord] = useState(null);

  const lastParams = useRef(null);

  const fmt = (dt) => dt.replace("T", " ") + ":00";

  useEffect(() => {
    (async () => {
      try {
        const response = await getAlarmTypeList();
        if (response.code === 0) setAlarmTypes(response.data || []);
      } catch (err) {
        console.error("Alarm type fetch failed", err);
      }
    })();
  }, []);

  const buildParams = useCallback(
    (pageIndex = 1) => ({
      deviceIds: deviceId,
      queryType: "Device",
      alarmType: alertType,
      startTime: fmt(startDate),
      endTime: fmt(endDate),
      operates: status ? Number(status) : undefined,
      queryInvalidStatus: 0,
      pageIndex,
    }),
    [deviceId, alertType, startDate, endDate, status]
  );

  const loadPage = useCallback(async (pageIndex, params) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        queryType: "Device",
        deviceIds: params.deviceIds,
        alarmType: params.alarmType || "",
        alarmIds: null,
        startTime: params.startTime,
        endTime: params.endTime,
        operates: params.operates,
        pageSize: PAGE_SIZE,
        pageIndex,
        queryInvalidStatus: params.queryInvalidStatus,
      };

      const response = await getAlarmPage(payload);
      if (response.code !== 0) throw new Error(response.msg || "API error");

      const result = response.data;
      const recs = result.records ?? [];
      setRecords(recs);
      setTotal(result.total ?? 0);

      if (recs.length === 0) {
        setFileMap({});
        return;
      }

      const alarmIds = recs.map((r) => r.alarmId).join(",");
      const filesResponse = await getAlarmFiles({
        deviceIds: params.deviceIds,
        alarmType: params.alarmType || "",
        startTime: params.startTime,
        endTime: params.endTime,
        queryInvalidStatus: params.queryInvalidStatus,
        alarmIds,
      });
      if (filesResponse.code !== 0)
        throw new Error(filesResponse.msg || "API error");

      const map = {};
      for (const item of filesResponse.data || []) {
        map[item.alarmId] = item.dailyAlarmFileList ?? [];
      }
      setFileMap(map);
    } catch (err) {
      console.error(err);
      setError(err.message ?? "Failed to fetch events. Please try again.");
      setRecords([]);
      setFileMap({});
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const runSearch = useCallback(async () => {
    const params = buildParams(1);
    lastParams.current = params;
    setPage(1);
    await loadPage(1, params);
  }, [buildParams, loadPage]);

  // Auto-load whenever the focused device changes
  useEffect(() => {
    if (!deviceId) return;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const handlePageChange = useCallback(
    async (_, newPage) => {
      setPage(newPage);
      if (lastParams.current) await loadPage(newPage, lastParams.current);
    },
    [loadPage]
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const selectSx = {
    height: 30,
    fontSize: "12px",
    borderRadius: "6px",
    background: "#fff",
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#f6f7fb",
        overflow: "hidden",
      }}
    >
      {/* Drag handle + header */}
      <Box
        sx={{
          flexShrink: 0,
          background: "#fff",
          borderBottom: "1px solid #e7eaf2",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: "6px",
            pb: "2px",
          }}
        >
          <Box
            sx={{ width: 36, height: 4, borderRadius: 3, background: "#dfe3ee" }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: "16px", py: "8px" }}
        >
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "8px",
                background: "#082D6B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FlagIcon sx={{ fontSize: 14, color: "#fff" }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: "13.5px", fontWeight: 700, color: "#1a2540" }}
              >
                Events
              </Typography>
              <Typography sx={{ fontSize: "10.5px", color: "#8a93a8" }}>
                {deviceName || deviceId}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="datetime-local"
              size="small"
              value={startDate}
              onChange={(e) =>
                setRange((p) => ({ ...p, startDate: e.target.value }))
              }
              sx={{
                width: 150,
                "& .MuiInputBase-root": { height: 30, fontSize: "11.5px" },
              }}
            />
            <Typography sx={{ color: "#b6bfd4", fontSize: 12 }}>–</Typography>
            <TextField
              type="datetime-local"
              size="small"
              value={endDate}
              onChange={(e) =>
                setRange((p) => ({ ...p, endDate: e.target.value }))
              }
              sx={{
                width: 150,
                "& .MuiInputBase-root": { height: 30, fontSize: "11.5px" },
              }}
            />

            <FormControl size="small" sx={{ width: 130 }}>
              <Select
                value={alertType}
                onChange={(e) => setAlertType(e.target.value)}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="">
                  <em style={{ color: "#94a3b8" }}>All types</em>
                </MenuItem>
                {alarmTypes.map((t) => (
                  <MenuItem key={t.formatKey} value={t.formatKey}>
                    {t.formatValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 110 }}>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                displayEmpty
                sx={selectSx}
              >
                <MenuItem value="">
                  <em style={{ color: "#94a3b8" }}>All status</em>
                </MenuItem>
                <MenuItem value="1">Completed</MenuItem>
                <MenuItem value="2">Pending</MenuItem>
                <MenuItem value="3">Processing</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Search">
              <IconButton
                onClick={runSearch}
                size="small"
                sx={{
                  width: 30,
                  height: 30,
                  background: "#082D6B",
                  color: "#fff",
                  borderRadius: "7px",
                  "&:hover": { background: "#0a3580" },
                }}
              >
                {loading ? (
                  <CircularProgress size={13} color="inherit" />
                ) : (
                  <SearchIcon sx={{ fontSize: 15 }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Collapse">
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  width: 30,
                  height: 30,
                  color: "#8a93a8",
                  border: "1px solid #e7eaf2",
                  borderRadius: "7px",
                }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "16px", py: "12px" }}>
        {error && (
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              p: 1.2,
              borderRadius: "8px",
              background: "#fce8e6",
              border: "1px solid #f5c6c2",
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "#b91c1c" }}>
              ⚠ {error}
            </Typography>
          </Paper>
        )}

        {loading && records.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={26} sx={{ color: "#082D6B" }} />
          </Box>
        )}

        {!loading && records.length === 0 && !error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              color: "#b6bfd4",
            }}
          >
            <SearchIcon sx={{ fontSize: 38, mb: 1 }} />
            <Typography sx={{ fontSize: "12.5px", color: "#94a3b8" }}>
              No events found for this vehicle in the selected range
            </Typography>
          </Box>
        )}

        <Fade in={!loading || records.length > 0}>
          <Box
  sx={{
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gridTemplateRows: "repeat(2, 1fr)",
    gap: "12px",
    height: "100%",
  }}
>
            {records.map((rec) => (
              <MiniEventCard
                key={rec.alarmId}
                record={rec}
                fileMap={fileMap}
                onClick={() => {
                  setModalRecord(rec);
                  setModalOpen(true);
                }}
              />
            ))}
          </Box>
        </Fade>
      </Box>

      {/* Footer / pagination */}
      {total > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            flexShrink: 0,
            py: "8px",
            borderTop: "1px solid #e7eaf2",
            background: "#fff",
          }}
        >
          <Typography sx={{ fontSize: "11px", color: "#8a93a8" }}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of{" "}
            {total}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size="small"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { fontSize: "11px", minWidth: 24, height: 24 },
              "& .Mui-selected": {
                background: "#082D6B !important",
                color: "#fff",
              },
            }}
          />
        </Stack>
      )}

      <EventDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        records={records}
        fileMap={fileMap}
        initialRecord={modalRecord}
      />
    </Box>
  );
};

export default EventsSlidePanel;